import angular from 'angular';
import _ from 'lodash-es';
import moment from 'moment';
import kformUtils from '../utils/kform-utils';

/**
 * @typedef KformComponentControllerOptionsScope
 * @property {boolean} [builder]
 * @property {boolean} [building]
 */

/**
 * @typedef KformComponentControllerScope
 * @property {import('angular').IFormController} $parent
 * @property {Function} addFieldValue
 * @property {any} builder
 * @property {Function} checkConditional
 * @property {any} component
 * @property {string} componentId
 * @property {any} data
 * @property {any} form
 * @property {string} formName
 * @property {Function} getInputGroupStyles
 * @property {Function} getLabelStyles
 * @property {Function} getOptionInputStyles
 * @property {Function} getOptionLabelStyles
 * @property {number} gridCol
 * @property {number} gridRow
 * @property {any[]} hideComponents
 * @property {Function} invalidQuestions
 * @property {import('angular').IFormController} kformForm
 * @property {Function} isDisabled
 * @property {Function} isRequired
 * @property {Function} isVisible
 * @property {Function} labelVisible
 * @property {KformComponentControllerOptionsScope} options
 * @property {boolean} readOnly
 * @property {Function} removeFieldValue
 * @property {Function} resetForm
 * @property {any} submission
 * @property {string} template
 * @property {Function} topOrLeftOptionLabel
 */

export default class KformComponentController {
	/* @ngInject */
	/**
	 * @param {import('angular').IControllerService} $controller
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {any} KformComponents
	 */
	constructor(
		$controller,
		$scope,
		$timeout,
		KformComponents,
	) {
		this.$scope = $scope;
		this.component = $scope.component;
		this.data = $scope.data;
		this.index = $scope.index;
		this.form = $scope.form;
		this.hideComponents = $scope.hideComponents;
		this.kformForm = $scope.$parent[$scope.formName];
		this.krepre_mode = undefined;
		this.options = $scope.options || {};
		this.submission = $scope.submission;
		this.parent = $scope.$parent;
		this.readOnly = $scope.readOnly;

		if ($scope.builder === undefined) {
			this.builder = this.options.builder === true;
		}

		// Pass through checkConditional since this is an isolate scope.
		this.checkConditional = $scope.$parent.checkConditional;

		// FOR-71 - Dont watch in the builder view.
		// Calculate value when data changes.
		if (!this.options.building && (this.component.calculateValue || _.get(this.component, 'validate.json'))) {
			$scope.$watch('submission.data', () => {
				kformUtils.checkCalculated(this.component, this.submission, this.data);

				// Process jsonLogic stuff if present.
				if (_.get(this.component, 'validate.json')) {
					let input;

					// Only json parse once.
					if (typeof this.component.validate.json === 'string') {
						try {
							input = JSON.parse(this.component.validate.json);
							this.component.validate.json = input;
						}
						catch {
							/* eslint-disable no-console */
							console.warn(`Invalid JSON validator given for ${ this.component.key}`);
							console.warn(this.component.validate.json);
							/* eslint-enable no-console */
							delete this.component.validate.json;
							return;
						}
					}
					else {
						input = this.component.validate.json;
					}

					let valid = true;
					try {
						if (this.component.type === 'datetime') {
							this.data[this.component.key] = moment(this.data[this.component.key]).toISOString();
						}
						valid = kformUtils.jsonLogic.apply(input, {
							data: this.submission ? this.submission.data : this.data,
							row: this.data,
						});
					}
					catch (/** @type {any} */ error) {
						valid = error.message;
					}

					$timeout(() => {
						try {
							if (valid !== true) {
								this.component.customError = valid;
								this.kformForm[this.componentId].$setValidity('custom', false);
								return;
							}

							this.kformForm[this.componentId].$setValidity('custom', true);
						}
						catch {
							// Ignore any issues while editing the components.
						}
					});
				}
			}, true);
		}

		this.topOrLeftOptionLabel = kformUtils.optionsLabelPositionWrapper(kformUtils.topOrLeftOptionLabel);
		this.getOptionLabelStyles = kformUtils.optionsLabelPositionWrapper(kformUtils.getOptionLabelStyles);
		this.getOptionInputStyles = kformUtils.optionsLabelPositionWrapper(kformUtils.getOptionInputStyles);

		// Get the settings.
		const component = KformComponents.components[this.component.type] || KformComponents.components.custom;

		// Set the component with the defaults from the component settings.
		// Dont add the default key, so that components without keys will remain visible by default.
		for (const [key, value] of Object.entries(component.settings)) {
			if (!Object.prototype.hasOwnProperty.call(this.component, key) && key !== 'key') {
				this.component[key] = angular.copy(value);
			}
		}

		// Set the template for the component.
		this.template = typeof component.template === 'function' ? component.template($scope) : component.template;

		// Allow component keys to look like "settings[username]"
		if (this.component.key && this.component.key.includes('[')) {
			const matches = this.component.key.match(/([^[]+)\[([^]+)]/);
			if ((matches.length === 3) && Object.prototype.hasOwnProperty.call(this.data, matches[1])) {
				this.data = this.data[matches[1]];
				this.component.key = matches[2];
			}
		}

		// If the component has a controller.
		if (component.controller) {
			// Maintain reverse compatibility by executing the old method style.
			if (typeof component.controller === 'function') {
				component.controller($scope.component, $scope);
			}
			else {
				$controller(component.controller, { $scope, $timeout });
			}
		}

		// Set the component name.
		this.componentId = $scope.component.key;
		if ($scope.gridRow !== undefined) {
			this.componentId += (`-${ $scope.gridRow}`);
		}
		if ($scope.gridCol !== undefined) {
			this.componentId += (`-${ $scope.gridCol}`);
		}
		$scope.componentId = this.componentId;
		$scope.isRequired = this.isRequired;
	}

	/**
	 * Add a new field value.
	 */
	addFieldValue(){
		/** @type {any} */
		const defaultData = { name: null };
		kformUtils.checkDefaultValue(this.component, this.submission, defaultData, this.$scope, () => {
			this.data[this.component.key] = this.data[this.component.key] ?? [];
			this.data[this.component.key].push(defaultData[this.component.key]);
		});
	}

	/**
	 * @param {any} component
	 * @returns {boolean}
	 */
	isRequired(component) {
		return kformUtils.isRequired(component);
	}

	/**
	 * Remove a field value.
	 *
	 * @param {number} index
	 */
	removeFieldValue(/** @type {number} */ index) {
		if (!Array.isArray(this.data[this.component.key])) {
			this.data[this.component.key] = [];
		}
		this.data[this.component.key].splice(index, 1);
	}

	// FOR-245 - Fix reset buttons.
	resetForm(){
		// Manually remove each key so we don't lose a reference to original
		// data in child scopes.
		for (const key in this.data) {
			if (Object.prototype.hasOwnProperty.call(this.data, key)) {
				delete this.data[key];
			}
		}

		// Set the form to pristine again.
		for (const compKey in this.kformForm) {
			if ('$setPristine' in this.kformForm[compKey]) {
				this.kformForm[compKey].$setPristine();
				this.kformForm[compKey].$setUntouched();
			}
		}
	}
}


